import React, { createContext, useContext, useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { capabilitiesService, CapabilitiesResponse, CapabilityState } from '../services/capabilities.service';

/**
 * Contexte des capacités du compte (saas/12, §4). Chargé une fois par session
 * depuis l'API — le serveur est la seule source de vérité, aucune liste en dur.
 *
 * En cas d'échec de chargement du catalogue : le front n'invente rien et
 * laisse les écrans s'afficher — c'est l'API qui fait autorité et refuse
 * (403) ce que le plan ne contient pas. Verrouiller le périmètre gratuit sur
 * une erreur réseau serait pire que d'afficher un écran dont les appels
 * échouent.
 */
interface CapabilitiesContextValue {
  loaded: boolean;
  data: CapabilitiesResponse | null;
  stateOf: (capability: string) => CapabilityState;
  refresh: () => Promise<void>;
}

const CapabilitiesContext = createContext<CapabilitiesContextValue>({
  loaded: false,
  data: null,
  stateOf: () => 'granted',
  refresh: async () => undefined,
});

export const useCapabilities = (): CapabilitiesContextValue => useContext(CapabilitiesContext);

/** Provider-route : enveloppe les routes owner (rend un <Outlet/>). */
export const CapabilitiesProvider: React.FC = () => {
  const [data, setData] = useState<CapabilitiesResponse | null>(null);
  const [loaded, setLoaded] = useState(false);

  const refresh = async () => {
    try {
      setData(await capabilitiesService.getCapabilities());
    } catch {
      setData(null); // échec fermé : rien n'est supposé accordé
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Catalogue indisponible → on laisse passer (l'API refusera d'elle-même).
  const stateOf = (capability: string): CapabilityState =>
    data?.capabilities?.[capability] ?? 'granted';

  return (
    <CapabilitiesContext.Provider value={{ loaded, data, stateOf, refresh }}>
      <Outlet />
    </CapabilitiesContext.Provider>
  );
};
